"use client";

import Link from "next/link";
import clsx from "clsx";

type ButtonCtaProps = {
	href?: string;
	label: string;
	isExternal?: boolean;
	onClick?: () => void;
	className?: string;
	type?: "button" | "submit" | "reset";
	disabled?: boolean;
	btnInsideClassName?: string;
};

export default function ButtonCta({
	href,
	label,
	isExternal = false,
	onClick,
	className = "",
	type = "button",
	disabled = false,
	btnInsideClassName = "",
}: ButtonCtaProps) {
	const handleClick = (e: React.MouseEvent) => {
		if (disabled) {
			e.preventDefault();
			e.stopPropagation();
			return;
		}
		onClick?.();
	};

	const buttonClasses = clsx(
		"group relative w-fit",
		"transition-all duration-300 ease-in-out",
		"px-[1.074rem] pt-[0.859rem] pb-3",
		"bg-olive-green-light rounded-[1.611rem]",
		!disabled && "hover:bg-olive-green hover:rounded-[0.269rem]",
		!disabled && "active:bg-olive-green active:rounded-[0.269rem]",
		disabled && "opacity-50 cursor-not-allowed",
		className,
	);

	const content = (
		<div className="relative flex h-full items-center gap-2">
			<span
				className={clsx(
					"text-cap-m transition-colors duration-300 ease-in-out",
					"text-olive-green",
					!disabled && "group-hover:text-olive-green-light",
					!disabled && "group-active:text-olive-green-light",
					btnInsideClassName,
				)}
			>
				{label}
			</span>
			<span
				className={clsx(
					"block h-[0.43rem] w-[0.43rem] rounded-full",
					"transition-colors duration-300 ease-in-out",
					"bg-olive-green",
					!disabled && "group-hover:bg-olive-green-light",
					!disabled && "group-active:bg-olive-green-light",
				)}
			/>
		</div>
	);

	if (href) {
		if (isExternal) {
			return (
				<a
					href={disabled ? undefined : href}
					onClick={handleClick}
					className={buttonClasses}
					target="_blank"
					rel="noopener noreferrer"
					aria-disabled={disabled}
				>
					{content}
				</a>
			);
		}
		return (
			<Link href={href} className={buttonClasses} onClick={handleClick}>
				{content}
			</Link>
		);
	}

	return (
		<button type={type} onClick={handleClick} className={buttonClasses} disabled={disabled}>
			{content}
		</button>
	);
}
